import { useState, useCallback, useRef, useMemo } from 'react';
import { useSnackbar } from '../utilities/snackbar_provider';
import axios from 'axios';

const useApi = (action, options) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!options?.initialLoad);
  const [error, setError] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Use a ref for options to ensure the stable fetchData function
  // always has access to the latest callbacks without being re-created.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const fetchData = useCallback(async (body = null, id) => {
    try {
      setLoading(true);
      if (!body) {
        body = {};
      }
      const res = await action(body, id);

      const isAxiosError = axios.isAxiosError(res);
      const isStockError = res instanceof Error;

      if (!isStockError && !isAxiosError) {
        if (!optionsRef.current?.noStore) {
          setData(res);
        }
        if (optionsRef.current?.success) {
          let message = typeof res?.data === "string"
            ? res.data
            : typeof res?.message === "string" ? res?.message : `${optionsRef.current?.actionName ?? "Action"} Successful!${optionsRef.current?.additionalActionName ? ` ${optionsRef.current.additionalActionName}` : ""}`;

          showSnackbar(message, "success");
        }
        if (optionsRef.current?.onComplete) {
          optionsRef.current.onComplete(res, false)
        }
        setError(false);
      } else if (isAxiosError && res?.response?.status && res?.response?.status >= 400) {
        let message = null;
        if (res.response?.status === 404) {
          // 404s are silently ignored; callers handle missing data via empty state
        } else {
          const _d = res.response?.data;
          const _fallback = optionsRef.current?.errorMessage ?? `${optionsRef.current?.actionName ? optionsRef.current?.actionName : "Action"} Failed!`;
          if (typeof _d === 'string') {
            message = _d;
          } else {
            const detailMessages = Array.isArray(_d?.details)
              ? _d.details.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
              : [];
            const errorMessages = Array.isArray(_d?.errors)
              ? _d.errors.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean)
              : [];
            message = detailMessages.length ? detailMessages.join('. ')
              : errorMessages.length ? errorMessages.join('. ')
              : (typeof _d?.error === 'string' ? _d.error : _d?.error?.message || _d?.msg || _d?.message)
              ?? _fallback;
          }
          if (optionsRef.current?.error) {
            showSnackbar(message, "error");
          }
        }
        if (optionsRef.current?.onError) {
          optionsRef.current.onError(message, true)
        }
      } else {
        if (optionsRef.current?.error) {
          showSnackbar(res?.message || "Action Failed", "error");
        }
        throw new Error(res.message);
      }
    } catch (error) {
      setError(true);
      if (optionsRef.current?.onError) {
        optionsRef.current.onError('Action Failed', true)
      }
    } finally {
      setLoading(false);
    }
  }, [action, showSnackbar]); // action is usually stable as it's an imported function

  const clearError = useCallback(() => {
    setError(false);
  }, []);

  const clearLoading = useCallback(() => {
    setLoading(false);
  }, []);

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  const clearAll = useCallback(() => {
    setError(false);
    setLoading(false);
    setData(null);
  }, []);

  const clearActions = useMemo(() => ({
    clearError, clearLoading, clearData, clearAll
  }), [clearError, clearLoading, clearData, clearAll]);

  // Return all the necessary values and functions
  return {
    data,
    loading,
    error,
    action: fetchData,
    clearActions,
    snackbarComponent: null,
  };
};

export default useApi;
